import React, { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { getStoragekeysNegociations } from "~pages/PricesTableV2/helpers/storage";
import FiltroPrecoCliente from "~pages/PricesTableV2/components/filters/FiltroPrecoCliente";
import useModal from "~components/Modal/hooks/useModal";
import useNegociacaoPrecoRegra from "~fetchs/useNegociacaoPrecoRegra";
import usePrecoRegra from "~fetchs/usePrecoRegraV2";
import useTabelaSolicitacoes from "~fetchs/useTabelaSolicitacoes";
import SolicitacoesListagem from "../../components/Solicitacoes/SolicitacoesListagem";
import PricesTableSolicitarNegociacaoModal from "~pages/PricesTableV2/components/Modal/SolicitarNegociacaoModal";
import { SaveIndexBD } from "~services/utils/SaveIndexBD";
import { initialPath } from "~misc/paths";

const ClientRequestsPage = () => {
  const { id } = useParams();
  const [skip, setSkip] = useState(0);
  const take = 5;
  const [loading, setLoading] = useState(false);
  const [dadosHeader, setDadosHeader] = useState({});
  const [loadingFilters, setLoadingFilters] = useState(false);
  const { isModalOpen, closeModal, setModalOpen } = useModal();
  const navigate = useNavigate();
  const handleAddButton = (grupoProdutoId, produtoId, filialId, clienteId, origem) => {
    const dadosHeader = { grupoProdutoId, produtoId, filialId, clienteId, origem };

    if (origem === "editar" && clienteId) {
      SaveIndexBD("dados-selecionados-negociacao", dadosHeader);
      navigate(`${initialPath}/tabela-preco/${clienteId}/editar-negociacao`);
      return;
    }

    if (origem === "nova" && clienteId) {
      SaveIndexBD("dados-selecionados-negociacao", dadosHeader);
      navigate(`${initialPath}/tabela-preco/${clienteId}/nova-negociacao`);
      return;
    }

    setModalOpen(true);
    setDadosHeader(dadosHeader);
  };

  const keys = useMemo(() => getStoragekeysNegociations("historicoDeSolicitacoesPreco"), []);

  const getDefaultValues = () => {
    try {
      const savedProduto = localStorage.getItem(keys.produto);
      const savedGrupoProduto = localStorage.getItem(keys.grupoProduto);
      const savedFilial = localStorage.getItem(keys.filiais);
      const savedNegociacao = localStorage.getItem(keys.negociacao);
      const savedPeriodo = localStorage.getItem(keys.periodo);
      const savedMedida = localStorage.getItem(keys.medida);
      const savedStatus = localStorage.getItem(keys.status);

      return {
        cliente: id,
        grupoProduto: savedGrupoProduto ? JSON.parse(savedGrupoProduto).value : 0,
        produto: savedProduto ? JSON.parse(savedProduto).value : 0,
        filial: savedFilial ? JSON.parse(savedFilial).value : 0,
        tipoSolicitacao: savedNegociacao ? JSON.parse(savedNegociacao).value : 0,
        ordem: "recentes",
        statusSolicitacao: savedStatus ? JSON.parse(savedStatus).value : -1,
        periodo: savedPeriodo ? JSON.parse(savedPeriodo) : { value: 0, label: "Todos", startDate: null, endDate: null },
        unidadeMedida: savedMedida ? JSON.parse(savedMedida).value : 0,
      };
    } catch (error) {
      console.warn("Error loading filters from localStorage:", error);
      return {
        cliente: id,
        grupoProduto: 0,
        produto: 0,
        filial: 0,
        tipoSolicitacao: 0,
        ordem: "recentes",
        statusSolicitacao: -1,
        periodo: { value: 0, label: "Todos", startDate: null, endDate: null },
        medida: 0,
      };
    }
  };

  const methods = useForm({
    defaultValues: getDefaultValues(),
  });

  const grupoProduto = methods.watch("grupoProduto");
  const produto = methods.watch("produto");
  const filial = methods.watch("filial");
  const tipoSolicitacao = methods.watch("tipoSolicitacao");
  const ordem = methods.watch("ordem");
  const periodo = methods.watch("periodo");
  const unidadeMedida = methods.watch("unidadeMedida");
  const statusSolicitacao = methods.watch("statusSolicitacao");

  const filters = useMemo(
    () => ({
      cliente: id,
      grupoProduto: grupoProduto || 0,
      produto: produto || 0,
      filial: filial || 0,
      tipoSolicitacao: tipoSolicitacao || 0,
      ordem: ordem || "recentes",
      periodo: periodo?.value || 0,
      startDate: periodo?.startDate || null,
      endDate: periodo?.endDate || null,
      unidadeMedida: unidadeMedida || 0,
      statusSolicitacao: statusSolicitacao || -1,
    }),
    [
      id,
      grupoProduto,
      produto,
      filial,
      tipoSolicitacao,
      ordem,
      periodo?.value,
      periodo?.startDate,
      periodo?.endDate,
      unidadeMedida,
      statusSolicitacao,
    ]
  );

  const precoRegraResult = usePrecoRegra({ filters: filters, setLoading: setLoadingFilters }) || {};
  const { filtrosDisponiveisPrecoRegra } = precoRegraResult;
  const negociacaoResult = useNegociacaoPrecoRegra({ clienteId: id, skip, take }) || {};
  const { negociacoesAll } = negociacaoResult;
  const solicitacoesResult =
    useTabelaSolicitacoes({
      filters,
      skip,
      take,
      setLoading,
      negociacoes: negociacoesAll,
      clienteId: id,
      type: "tabela-preco",
    }) || {};
  const { solicitacoesQuery, solicitacoesQueryAll } = solicitacoesResult;

  return (
    <div className="client-details-page">
      <FormProvider {...methods}>
        <FiltroPrecoCliente
          filters={filters}
          nomeDoFiltro={"historicoDeSolicitacoesPreco"}
          solicitacao
          filtrosDisponiveis={filtrosDisponiveisPrecoRegra}
          loadingx={loadingFilters}
        />

        <SolicitacoesListagem
          handleAddButton={handleAddButton}
          solicitacoes={solicitacoesQuery || []}
          solicitacoesAll={solicitacoesQueryAll}
          loading={loading}
          skip={skip}
          take={take}
          setSkip={setSkip}
          setLoading={setLoading}
        />

        <PricesTableSolicitarNegociacaoModal isOpen={isModalOpen} closeModal={closeModal} dadosHeader={dadosHeader} />
      </FormProvider>
    </div>
  );
};

export default ClientRequestsPage;
